import React, { useEffect, useState } from 'react';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import CustomCircularProgress from 'components/CustomCircularProgress/CustomCircularProgress';
import CustomRadio from 'components/CustomRadio/CustomRadio';
import ServicePicker from 'components/ServicePicker';
import { CustomAttribute } from 'utils/ecommerce/services/extract';
import PriceCell from '../common/cells/PriceCell';
import { attributes } from './constants';
import { ProductsService } from 'services/products/products';
import { finalize } from 'rxjs/operators';
import style from 'assets/jss/material-dashboard-pro-react/components/postpaidLinesStyle';
import { makeStyles } from '@material-ui/core';
import { translate } from 'components/Translation';
import { getLanguage, Language } from 'utils/language/language';
import {
	BasicPlan,
	Commitments,
	ProductsDetailsResponse,
	TariffPlan,
	ProductInfo,
	PlanCommitments,
} from 'utils/api/models/postpaidLines/postpaidLines';
import SimpleValueCell from '../common/cells/SimpleValueCell';
import DiscountCell from '../common/cells/DiscountCell';
import numeral from 'numeral';
import { BillingType } from 'utils/api/models/accounts/singleAccount';
import { BodyS } from 'components/Typography/Typography';
import { CommitmentDetails } from 'utils/api/models/payments/commitmentDetails';

//@ts-ignore
const useStyle = makeStyles(style);

interface IPostpaidLinesTableProps {
	accountId: string | undefined;
	setSelectedTariff: React.Dispatch<React.SetStateAction<BasicPlan | null>>;
	setSelectedCommitment: React.Dispatch<React.SetStateAction<Commitments | null>>;
	setProductsDetails?: React.Dispatch<React.SetStateAction<ProductsDetailsResponse | null>>;
	selectedProductFilter?: string;
	setCommitmentDuration: React.Dispatch<React.SetStateAction<string | null>>;
	currentPlanId?: string;
	selectedTariff?: BasicPlan | null;
	commitmentDuration?: string | null;
	commitment: number | null;
	setCommitment: React.Dispatch<React.SetStateAction<number | null>>;
	commitmentDetails?: CommitmentDetails[];
}

export const PostpaidLinesTable = ({
	accountId,
	setSelectedTariff,
	setSelectedCommitment,
	setProductsDetails,
	selectedProductFilter,
	setCommitmentDuration,
	currentPlanId,
	selectedTariff,
	commitmentDuration,
	commitment,
	setCommitment,
	commitmentDetails,
}: IPostpaidLinesTableProps) => {
	const [loading, setLoading] = useState<boolean>(false);
	const [productsData, setProductsData] = useState<TariffPlan[] | null>(null);
	const [selectedTab, setSelectedTab] = useState<string | null>(selectedTariff ? selectedTariff.productId : null);
	const [productsDataByCommitment, setProductsDataByCommitment] = useState<BasicPlan[]>([]);
	const [numberOfPlansPerPage, setNumberOfPlansPerPage] = useState<number>(1);
	const [currentProductPrice, setCurrentProductPrice] = useState<number>(0);

	const lang = getLanguage();

	const classes = useStyle();
	const selectedProductData = productsData?.find((pd) => pd.name === selectedProductFilter);
	const getNumberOfPlansPerPage = () => {
		if (window.innerWidth < 960 && window.innerWidth > 480) {
			setNumberOfPlansPerPage(3);
		} else if (window.innerWidth < 480 && selectedProductData) {
			setNumberOfPlansPerPage(1);
		} else if (selectedProductData) {
			setNumberOfPlansPerPage(
				selectedProductData.basicPlans.length <= 5 ? Math.max(selectedProductData.basicPlans.length, 1) : 5
			);
		}
	};
	const radioButton = (index: number, planCommitment: PlanCommitments) => (
		<div key={index}>
			<CustomRadio
				label={translate(`new.postpaid.line.table.commitment.${planCommitment.commitmentDuration}`)}
				checked={(!!commitment ? commitment.toString() : null) === planCommitment.commitmentDuration}
				onChange={() => handleSetCommitment(planCommitment)}
			/>
		</div>
	);

	useEffect(() => {
		getNumberOfPlansPerPage();
	}, [selectedProductData]);

	window.addEventListener('resize', function() {
		getNumberOfPlansPerPage();
	});

	useEffect(() => {
		const currentProduct = (productsData || [])
			.map((tariffPlan) => tariffPlan.basicPlans)
			.flat()
			.find((product) => product.productId === currentPlanId);

		if (currentProduct) {
			setCurrentProductPrice(+currentProduct.postpaidDetails.amount);
		}
	}, [productsData, currentPlanId]);

	useEffect(() => {
		if (accountId) {
			setLoading(true);
			const subscription = ProductsService.getProductsDetails(accountId, BillingType.POSTPAID)
				.pipe(
					finalize(() => {
						setLoading(false);
					})
				)
				.subscribe(({ productDetails }) => {
					if (setProductsDetails) {
						setProductsDetails(productDetails);
					}
					setProductsData(productDetails.tariffPlans);
				});
			return () => {
				subscription.unsubscribe();
			};
		}
	}, [accountId, setProductsDetails]);

	const sortProductsDataByAmount = (a: BasicPlan, b: BasicPlan) => {
		return +a.postpaidDetails.amount - +b.postpaidDetails.amount;
	};

	useEffect(() => {
		if (productsData && selectedProductData) {
			const planCommitments = selectedProductData.planCommitments.find(
				(planCommitment) =>
					planCommitment.commitmentDuration === (commitment !== null ? commitment.toString() : null)
			);
			if (planCommitments) {
				const ids = planCommitments.commitments.map((cm) => cm.parentPlanProductId);
				const productsFilteredByCommitment = selectedProductData.basicPlans.filter((product) => {
					return ids.includes(product.productId) ? product : null;
				});
				if (productsFilteredByCommitment.length > 0) {
					productsFilteredByCommitment.sort(sortProductsDataByAmount);
					setProductsDataByCommitment(productsFilteredByCommitment);
				}
			} else {
				setProductsDataByCommitment([]);
			}
		}
	}, [productsData, commitment, selectedProductData]);

	const getCell = (product: ProductInfo[], cellName: string, index: number) => {
		if (product !== undefined) {
			let singleProduct = null;
			const filteredProduct = product.filter((singleProduct) => singleProduct.propertyName === cellName);
			if (filteredProduct.length === 1) {
				singleProduct = filteredProduct[0];
			} else if (filteredProduct.length > 1) {
				const filteredProductValues = filteredProduct.map((singleProduct) => +singleProduct.propertyValue);
				singleProduct = {
					propertyName: filteredProduct[0].propertyName,
					propertyValue: filteredProductValues.reduce((acc, cur) => acc + cur).toString(),
				};
			}
			if (singleProduct) {
				if (cellName === CustomAttribute.INTERNATIONAL_CALL) {
					return (
						<DiscountCell
							key={index}
							backgroundColor={index % 2 === 0 ? 'backgroundGray' : 'white'}
							discount={parseInt(singleProduct.propertyValue)}
							subtitle={translate('services.table.fbb.discount.subtitle')}
						/>
					);
				}
				return (
					<SimpleValueCell
						key={index}
						backgroundColor={index % 2 === 0 ? 'backgroundGray' : 'white'}
						value={singleProduct.propertyValue ? singleProduct.propertyValue : '-'}
						subtitle={translate(`services.table.postpaid.line.${cellName}`)}
						minHeight={35}
					/>
				);
			}
		}
		return (
			<SimpleValueCell
				key={index}
				backgroundColor={index % 2 === 0 ? 'backgroundGray' : 'white'}
				value={'-'}
				subtitle={translate(`services.table.postpaid.line.${cellName}`)}
				minHeight={35}
			/>
		);
	};

	const getCellForCommitmentBenefits = (planCommitment: PlanCommitments | undefined, product: BasicPlan) => {
		if (planCommitment) {
			const commitment = planCommitment.commitments.find((c) => c.parentPlanProductId === product.productId);
			if (commitment) {
				return commitment.productBenefits.map((pb, index) => (
					<SimpleValueCell
						key={index}
						backgroundColor={index % 2 === 0 ? 'backgroundGray' : 'white'}
						value={pb.freeUnitsProductName}
						subtitle={translate(`services.table.postpaid.line.bonus`)}
						minHeight={35}
					/>
				));
			}
		}
	};

	useEffect(() => {
		const activeCommitmentDuration =
			commitmentDetails && commitmentDetails.length ? commitmentDetails[0].commitmentDetails[0].totalPeriods : 0;

		const lowestAvailableCommitment = selectedProductData?.planCommitments.find(
			(pc) => +pc.commitmentDuration >= activeCommitmentDuration
		);

		const availableCommitmentDuration =
			lowestAvailableCommitment && lowestAvailableCommitment.commitmentDuration
				? +lowestAvailableCommitment.commitmentDuration
				: null;

		setCommitment(availableCommitmentDuration);
	}, [commitmentDetails, setCommitment, selectedProductData]);

	useEffect(() => {
		if (commitmentDetails && commitmentDetails.length) {
			const commitmentDuration = selectedProductData?.planCommitments.find(
				(pc) => +pc.commitmentDuration >= commitmentDetails[0].commitmentDetails[0].totalPeriods
			);
			if (commitmentDuration) setCommitment(+commitmentDuration.commitmentDuration);
		}
	}, [selectedProductData]);

	const handleSetCommitment = (planCommitment: PlanCommitments) => {
		setSelectedTariff(null);
		setSelectedTab(null);
		setCommitment(planCommitment.commitmentDuration !== null ? Number(planCommitment.commitmentDuration) : null);
	};

	return (
		<ConditionalRender
			show={loading}
			onTrue={() => <CustomCircularProgress loading text="" transparent />}
			onFalse={() => (
				<div>
					<div className={classes.commitmentPicker}>
						<div className={classes.text}>
							<BodyS>{translate(`new.postpaid.line.select.plan`)}</BodyS>
						</div>
						<div className={classes.radioButtons}>
							<ConditionalRender
								show={!!selectedProductData}
								onTrue={() =>
									selectedProductData!.planCommitments.map((planCommitment, index) => {
										if (planCommitment && commitmentDetails && commitmentDetails.length) {
											if (
												+planCommitment.commitmentDuration >=
												commitmentDetails[0].commitmentDetails[0].totalPeriods
											) {
												return radioButton(index, planCommitment);
											} else {
												return <div></div>;
											}
										} else {
											return radioButton(index, planCommitment);
										}
									})
								}
							/>
						</div>
					</div>
					<div className={classes.servicePickerWrapper}>
						<div className={classes.servicePicker}>
							{selectedProductData && selectedProductData.basicPlans && (
								<ServicePicker
									selectedTab={productsDataByCommitment.findIndex(
										(product) => selectedTab === product.productId
									)}
									setSelectedTab={(index: number | null) => {
										setSelectedTab(
											index == null ? null : productsDataByCommitment[index].productId
										);
										setSelectedTariff(index == null ? null : productsDataByCommitment[index]);
										if (index && productsDataByCommitment[index]) {
											setSelectedCommitment(
												selectedProductData.planCommitments[
													selectedProductData.planCommitments.findIndex((pc) => {
														return commitment !== null
															? pc.commitmentDuration === commitment.toString()
															: pc.commitmentDuration == null;
													})
												].commitments.find(
													(c) =>
														c.parentPlanProductId ===
														productsDataByCommitment[index].productId
												)!
											);
											setCommitmentDuration(
												selectedProductData.planCommitments[
													selectedProductData.planCommitments.findIndex((pc) => {
														return commitment !== null
															? pc.commitmentDuration === commitment.toString()
															: pc.commitmentDuration == null;
													})
												].commitmentDuration
											);
										}
									}}
									tabs={productsDataByCommitment.map((product) => {
										const prices = Number(product.postpaidDetails.amount) / 1000;
										const price = {
											offer: prices,
											display: prices,
										};
										const planTariffId = product.productId;

										return {
											column: (
												<>
													{attributes.map((attribute, index) => (
														<div key={index}>
															{getCell(product.productInfo, attribute, index)}
														</div>
													))}
													<div>
														{getCellForCommitmentBenefits(
															selectedProductData.planCommitments.find((pc) =>
																commitment === null
																	? pc.commitmentDuration === null
																	: pc.commitmentDuration === commitment.toString()
															),
															product
														)}
													</div>
													<PriceCell
														price={numeral(price.display).format('0.000')}
														currency={'OMR'}
														perMonth
														backgroundColor="white"
													/>
												</>
											),
											numberOfCells: attributes.length + 1,
											header: {
												title: product?.productName,
												height: lang === Language.AR ? 220 : 100,
											},
											disabled:
												currentPlanId === planTariffId ||
												currentProductPrice > Number(product.postpaidDetails.amount),
										};
									})}
									numberOfTabsOnSlide={numberOfPlansPerPage}
								/>
							)}
						</div>
					</div>
				</div>
			)}
		/>
	);
};
